/*
 * Pre-checkout del mini-cart (Sifrah): hoja que sube desde abajo al pulsar «Continuar».
 *   Paso 1: envío a domicilio o recojo en tienda.
 *   Paso 2: ubigeo (departamento / provincia / distrito) o tienda de recojo. Si está activo
 *           settings.cart_document_field_enable, también documento de identidad y «Necesito factura».
 * Mismo flujo que el mini-cart de Carolina Store, con el contrato del backend de Sifrah:
 *   1. Ubigeos, tarifa y tiendas desde el app proxy de Apolo (departaments / provinces / distrits,
 *      district-calc-rates y shop-addresses).
 *   2. cart/update.js con los atributos de Sifrah ('Modalidad despacho', 'departamento',
 *      'tipo de envio', 'tienda recojo'…).
 *   3. cartCreate en la Storefront API con los atributos por línea que lee el CarrierService
 *      ('_tipo de despacho', '_shipping method', '_ubigeo', con guion bajo como en el tema
 *      publicado) y la dirección precargada. En recojo se
 *      preselecciona además la tarifa «Recojo en tienda» del carrier; ese carrito se arma en
 *      segundo plano apenas se elige la tienda, así «Ir a pagar» solo redirige.
 *   4. Redirección al checkout (+ UTMs). Si algo falla, cae al checkout nativo.
 * Lo elegido se guarda en localStorage (sifrah_delivery_prefs) y, con sesión iniciada, en los
 * metafields del cliente (scope=customer-prefs).
 */
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const API_BASE = `${ROOT}apps/apolo-ubigeo-calculation`;
  const STOREFRONT_GRAPHQL = 'https://sifrah-store.myshopify.com/api/2024-04/graphql.json';
  const STOREFRONT_TOKEN = '857943b7f6cb8bc1dc421e6a129ba03d';
  const COUNTRY_PROXY_ID = '1';
  const PREFS_KEY = 'sifrah_delivery_prefs';
  const REMOTE_PREFS_KEY = 'sifrah_mc_remote_prefs';
  const CACHE_PREFIX = 'sifrah_mc_cache:';
  const UBIGEO_CACHE_TTL = 24 * 60 * 60 * 1000;
  const STORES_CACHE_TTL = 6 * 60 * 60 * 1000;
  const RATES_CACHE_TTL = 30 * 60 * 1000;
  // Tope de espera a la tarifa de recojo del carrier al pulsar «Ir a pagar»: si demora, se
  // redirige igual (el checkout queda con la tarifa por defecto).
  const PICKUP_PRESELECT_TIMEOUT = 12000;
  const PREWARM_WAIT = 10000;
  // Espacio libre sobre la hoja (max-height del panel: calc(100% - 48px)) y alto mínimo de la
  // lista de tiendas en pantallas muy bajas (un poco más de una tienda).
  const SHEET_TOP_GAP = 48;
  const STORES_MIN_HEIGHT = 120;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
  // Plazo de entrega por zona (como Carolina Store): Lima Metropolitana (1501) y Callao (0701) vs.
  // provincias. Gana la primera regla cuyo prefijo coincida con el ubigeo. Reemplaza la descripción
  // combinada del backend («… en Lima & … a Provincia»); una descripción propia del método se respeta.
  const DELIVERY_TIMES = [
    { ubigeo_prefix: ['1501', '0701'], text: 'Desde 1 día hábil' },
    { ubigeo_prefix: '*', text: 'Desde 2 días hábiles' },
  ];
  const EXCLUDED_DEPARTMENTS = ['Amazonas', 'Apurímac', 'Huancavelica', 'Loreto', 'Madre de Dios', 'Pasco', 'Ucayali'];

  // Solo estas preferencias viajan a los metafields del cliente; el documento queda en el navegador.
  const METAFIELD_KEYS = [
    'modalidad',
    'departamento',
    'provincia',
    'distrito',
    'ubigeo',
    'last_pickup_store_id',
    'f_direccion',
    'f_referencia',
  ];

  // Atributos que dependen del modo o del comprobante: se vacían en cart/update.js cuando no
  // aplican, para que no quede el valor de una compra anterior.
  const MODE_ATTRIBUTES = ['direccion', 'referencia', 'tienda recojo', 'tipo de envio', 'ruc'];

  // Tipos de documento (mismas reglas que el precheckout anterior) y RUC para factura.
  const DOCUMENTS = {
    person: [
      { label: 'DNI', value: 'DNI', minLen: 8, maxLen: 8, numeric: true },
      { label: 'CE', value: 'CE', minLen: 9, maxLen: 9, numeric: true },
      { label: 'Pasaporte', value: 'Pasaporte', minLen: 12, maxLen: 15, numeric: true },
    ],
    company: { label: 'RUC', value: 'RUC', minLen: 11, maxLen: 11, numeric: true },
  };

  // Deja solo los caracteres que admite el documento (dígitos o alfanuméricos) y su largo máximo.
  // Se aplica en `input` y no en `keypress`: los teclados de Android no disparan keypress.
  function sanitizeDocument(value, doc) {
    if (!doc) return String(value || '');
    const pattern = doc.numeric === false ? /[^a-zA-Z0-9]/g : /[^0-9]/g;
    const clean = String(value || '').replace(pattern, '');
    return doc.maxLen ? clean.slice(0, doc.maxLen) : clean;
  }

  function isDocumentValid(value, doc) {
    if (!doc) return String(value || '').trim() !== '';
    const pattern = doc.numeric === false ? /^[a-zA-Z0-9]+$/ : /^[0-9]+$/;
    return value.length >= doc.minLen && value.length <= doc.maxLen && pattern.test(value);
  }

  function matchesUbigeo(prefix, ubigeo) {
    const prefixes = Array.isArray(prefix) ? prefix : [prefix];
    return prefixes.some((item) => item === '*' || String(ubigeo).startsWith(String(item)));
  }

  // Texto del plazo para el ubigeo: el de la zona si el backend manda la descripción combinada de
  // Lima y provincias (o ninguna); si no, la del backend tal cual. Es el texto que se muestra y el
  // que viaja al checkout en _dispatch_mode_description.
  function resolveMethodDescription(description, ubigeo) {
    const text = String(description || '').trim();
    const combined = !text || (text.includes('&') && /lima/i.test(text) && /provincia/i.test(text));
    if (!combined) return text;
    const zone = DELIVERY_TIMES.find((item) => matchesUbigeo(item.ubigeo_prefix, ubigeo));
    return zone ? zone.text : text;
  }

  function normalizeName(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // El backend escribe «Apurimac» sin tilde: se compara sin tildes ni mayúsculas.
  const EXCLUDED = EXCLUDED_DEPARTMENTS.map(normalizeName);

  /* ---------------- Almacenamiento ---------------- */

  function readPrefs() {
    try {
      return JSON.parse(window.localStorage.getItem(PREFS_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  function writePrefs(values) {
    const data = Object.assign(readPrefs(), values);
    try {
      window.localStorage.setItem(PREFS_KEY, JSON.stringify(data));
    } catch (error) {
      /* sin storage: el flujo sigue igual */
    }
    return data;
  }

  function cacheGet(key, maxAge) {
    try {
      const entry = JSON.parse(window.localStorage.getItem(CACHE_PREFIX + key));
      if (!entry || typeof entry.t !== 'number' || Date.now() - entry.t > maxAge) return null;
      return entry.v;
    } catch (error) {
      return null;
    }
  }

  function cacheSet(key, value) {
    try {
      window.localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
    } catch (error) {
      /* cuota llena o modo privado */
    }
  }

  /* ---------------- Peticiones ---------------- */

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
  }

  // Lista del app proxy (ubigeos, tiendas, tarifas). Con `ttl` se guarda en localStorage. El
  // backend a veces responde 200 con { status: 'error' }: eso es un error y no se cachea.
  function getProxyList(query, ttl) {
    if (ttl) {
      const hit = cacheGet(`proxy:${query}`, ttl);
      if (hit) return Promise.resolve(hit);
    }
    return getJSON(`${API_BASE}?${query}`).then((data) => {
      const list = Array.isArray(data) ? data : data && Array.isArray(data.items) ? data.items : null;
      if (!list) throw new Error('Respuesta inválida del app proxy');
      const active = list.filter((item) => item && (!('is_active' in item) || item.is_active));
      if (ttl && active.length) cacheSet(`proxy:${query}`, active);
      return active;
    });
  }

  function storefront(query, variables, signal, accept) {
    return fetch(STOREFRONT_GRAPHQL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: accept || 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query, variables }),
      signal,
    }).then((response) => {
      if (!response.ok) throw new Error(`Storefront API: ${response.status}`);
      return response;
    });
  }

  function storefrontJSON(query, variables, signal) {
    return storefront(query, variables, signal)
      .then((response) => response.json())
      .then((data) => {
        if (data.errors && data.errors.length) throw new Error(data.errors[0].message);
        return data.data;
      });
  }

  // Respuestas @defer (multipart/mixed) del Storefront API: une el payload inicial y los parches.
  function storefrontDeferred(query, variables, signal) {
    return storefront(query, variables, signal, 'multipart/mixed;deferSpec=20220824, application/json').then((response) => {
      const contentType = response.headers.get('content-type') || '';
      return response.text().then((text) => {
        if (contentType.indexOf('multipart/mixed') === -1) {
          const single = JSON.parse(text);
          if (single.errors && single.errors.length) throw new Error(single.errors[0].message);
          return single.data;
        }
        const boundaryMatch = contentType.match(/boundary=("?)([^;"]+)\1/);
        const boundary = `--${boundaryMatch ? boundaryMatch[2] : 'graphql'}`;
        let merged = null;
        text.split(boundary).forEach((part) => {
          const jsonStart = part.indexOf('{');
          if (jsonStart === -1) return;
          let chunk;
          try {
            chunk = JSON.parse(part.slice(jsonStart).trim());
          } catch (error) {
            return;
          }
          if (chunk.data) {
            merged = chunk.data;
          } else if (chunk.incremental) {
            chunk.incremental.forEach((patch) => {
              if (!patch.data || !merged) return;
              let target = merged;
              (patch.path || []).forEach((key) => {
                if (target) target = target[key];
              });
              if (target) Object.assign(target, patch.data);
            });
          }
        });
        return merged;
      });
    });
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(() => resolve(null), ms));
  }

  /* ---------------- Checkout ---------------- */

  // Los UTMs se capturan al cargar cualquier página: el mini-cart puede abrirse en todas.
  function captureUTMs() {
    try {
      const params = new URLSearchParams(window.location.search);
      const utms = {};
      UTM_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) utms[key] = value;
      });
      if (Object.keys(utms).length) sessionStorage.setItem('utm_params', JSON.stringify(utms));
    } catch (error) {
      /* noop */
    }
  }

  function appendUTMs(url) {
    let utms = {};
    try {
      utms = JSON.parse(sessionStorage.getItem('utm_params')) || {};
    } catch (error) {
      utms = {};
    }
    const params = Object.keys(utms).map((key) => `${key}=${encodeURIComponent(utms[key])}`);
    if (!params.length) return url;
    return url + (url.includes('?') ? '&' : '?') + params.join('&');
  }

  function getAppliedDiscountCode(cart) {
    const apps = (cart && cart.cart_level_discount_applications) || [];
    const byCode = apps.find((app) => app && app.type === 'discount_code' && app.title);
    if (byCode) return byCode.title;
    const codes = (cart && cart.discount_codes) || [];
    const applied = codes.find((code) => code && code.applicable !== false) || codes[0];
    return applied ? applied.code : null;
  }

  function toProvinceCode(region) {
    return region === 'Lima' || region === 'LIMA' ? 'PE-LIM' : region;
  }

  // Dirección para la Storefront API sin campos vacíos.
  function cleanAddress(address) {
    const clean = {};
    Object.keys(address).forEach((key) => {
      const value = String(address[key] || '').trim();
      if (value) clean[key] = value;
    });
    return clean;
  }

  // Propiedades de la línea del carrito (p. ej. _gift) para que lleguen al checkout.
  function lineProperties(item) {
    const reserved = ['_tipo de despacho', '_shipping method', '_ubigeo', '_dispatch_mode_description'];
    return Object.keys(item.properties || {})
      .filter((key) => {
        const value = item.properties[key];
        return !reserved.includes(key) && value != null && typeof value !== 'object' && String(value) !== '';
      })
      .map((key) => ({ key, value: String(item.properties[key]) }));
  }

  /* ---------------- Formato ---------------- */

  // Formato de moneda de la tienda (theme.moneyFormat, p. ej. "S/ {{amount}}"), igual que los
  // precios del carrito. Recibe céntimos.
  function formatMoney(cents) {
    const format = (window.theme && window.theme.moneyFormat) || 'S/ {{amount}}';
    const value = (Number(cents) || 0) / 100;
    const group = (number, decimals, thousands, decimal) => {
      const [integer, fraction] = number.toFixed(decimals).split('.');
      const withThousands = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${withThousands}${decimal}${fraction}` : withThousands;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return group(value, 2, "'", '.');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }

  const CART_CREATE = `
    mutation cartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { id checkoutUrl }
        userErrors { field message }
      }
    }
  `;

  const DELIVERY_ADDRESSES_ADD = `
    mutation cartDeliveryAddressesAdd($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {
      cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {
        cart { id }
        userErrors { field message }
      }
    }
  `;

  const DELIVERY_GROUPS = `
    query cartDeliveryGroups($cartId: ID!) {
      cart(id: $cartId) {
        id
        ... @defer {
          deliveryGroups(first: 10, withCarrierRates: true) {
            edges {
              node {
                id
                deliveryOptions { handle title deliveryMethodType }
              }
            }
          }
        }
      }
    }
  `;

  const SELECTED_DELIVERY_OPTIONS_UPDATE = `
    mutation cartSelectedDeliveryOptionsUpdate($cartId: ID!, $selectedDeliveryOptions: [CartSelectedDeliveryOptionInput!]!) {
      cartSelectedDeliveryOptionsUpdate(cartId: $cartId, selectedDeliveryOptions: $selectedDeliveryOptions) {
        cart { id checkoutUrl }
        userErrors { field message }
      }
    }
  `;

  class SifrahMinicartCheckout {
    constructor(root) {
      this.root = root;
      this.sheet = root.querySelector('[data-enova-checkout-sheet]');
      this.form = this.sheet.querySelector('[data-enova-checkout-form]');
      this.modesEl = this.sheet.querySelector('[data-enova-checkout-modes]');
      this.panels = {
        delivery: this.sheet.querySelector('[data-enova-checkout-panel="delivery"]'),
        pickup: this.sheet.querySelector('[data-enova-checkout-panel="pickup"]'),
      };
      this.selects = {
        region: this.sheet.querySelector('[data-enova-ubigeo="region"]'),
        province: this.sheet.querySelector('[data-enova-ubigeo="province"]'),
        district: this.sheet.querySelector('[data-enova-ubigeo="district"]'),
      };
      this.shippingEl = this.sheet.querySelector('[data-enova-checkout-shipping]');
      this.storesEl = this.sheet.querySelector('[data-enova-checkout-stores]');
      this.pickupSummaryEl = this.sheet.querySelector('[data-enova-pickup-summary]');
      this.summaryEl = this.sheet.querySelector('[data-enova-checkout-summary]');
      this.collapses = {};
      this.sheet.querySelectorAll('[data-enova-collapse]').forEach((el) => {
        this.collapses[el.dataset.enovaCollapse] = el;
      });
      this.subtitleEl = this.sheet.querySelector('.enova-minicart-sheet__subtitle');
      // Documento y factura: solo si settings.cart_document_field_enable está activo.
      this.documentEl = this.sheet.querySelector('[data-enova-checkout-document]');
      this.hasDocument = !!this.documentEl;
      this.documentFields = this.hasDocument
        ? {
            type: this.documentEl.querySelector('[data-enova-document="type"]'),
            number: this.documentEl.querySelector('[data-enova-document="number"]'),
            invoice: this.documentEl.querySelector('[data-enova-document="invoice"]'),
            companyField: this.documentEl.querySelector('[data-enova-document="company-field"]'),
            companyLabel: this.documentEl.querySelector('[data-enova-document="company-label"]'),
            company: this.documentEl.querySelector('[data-enova-document="company"]'),
          }
        : null;
      this.errorEl = this.sheet.querySelector('[data-enova-checkout-error]');
      this.submitBtn = this.sheet.querySelector('[data-enova-checkout-submit]');
      this.submitLabel = this.sheet.querySelector('[data-enova-checkout-submit-label]');
      this.innerEl = this.sheet.querySelector('[data-enova-checkout-inner]');
      this.actionsEl = this.sheet.querySelector('[data-enova-checkout-actions]');
      this.summariesEl = this.sheet.querySelector('[data-enova-summaries]');
      this.totalRow = this.sheet.querySelector('[data-enova-checkout-total-row]');
      this.totalValue = this.sheet.querySelector('[data-enova-checkout-total]');
      // En móvil el resumen va en el cuerpo (con scroll) y solo el Total y el botón quedan fijos.
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.msg = this.sheet.dataset;

      this.customerId = root.dataset.customerId || null;
      this.customerEmail = (root.dataset.customerEmail || '').trim();
      this.freeShippingLimit = parseFloat(root.dataset.freeShippingLimit) || 0;

      this.mode = null;
      this.methods = [];
      this.stores = [];
      this.documents = DOCUMENTS;
      this.ratesRequest = 0;
      this.initialized = false;
      // Secciones mostradas como resumen (datos guardados). Al editarlas se abren y quedan así.
      this.compact = { delivery: false, document: false };
      this.ubigeoLoaded = false;
      this.storesLoaded = false;
      this.prewarm = null;

      this.onKeyup = this.onKeyup.bind(this);
      root.addEventListener('click', this.onTriggerClick.bind(this), true);
      this.sheet.addEventListener('click', this.onSheetClick.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      // Desde que el cliente toca la hoja, lo suyo manda sobre lo que llegue de sus metafields.
      const markTouched = (event) => {
        if (event.isTrusted) this.touched = true;
      };
      this.form.addEventListener('input', markTouched, true);
      this.form.addEventListener('change', markTouched, true);
      this.modesEl.addEventListener('change', (event) => {
        if (event.target.name === 'enova-minicart-mode') this.setMode(event.target.value);
      });
      this.selects.region.addEventListener('change', () => this.onRegionChange());
      this.selects.province.addEventListener('change', () => this.onProvinceChange());
      this.selects.district.addEventListener('change', () => this.onDistrictChange());
      this.shippingEl.addEventListener('change', () => {
        this.clearError();
        this.renderTotal();
      });
      this.storesEl.addEventListener('change', () => {
        this.clearError();
        this.renderPickupSummary();
        this.renderSaveLinks();
        this.schedulePrewarm();
      });
      if (this.hasDocument) this.bindDocument();
      this.watchHeight();

      // Al volver del checkout con «atrás» (o el gesto de swipe en iOS/Android) la página
      // se restaura desde el bfcache tal cual se dejó: con el botón en «Procesando…».
      this.placeSummaries();
      const onViewportChange = () => {
        this.placeSummaries();
        this.scheduleFitStores();
      };
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', onViewportChange);
      else this.mobileQuery.addListener(onViewportChange);
      this.storesEl.addEventListener('scroll', () => this.renderStoresOverflow(), { passive: true });
      window.addEventListener('resize', () => this.scheduleFitStores());

      window.addEventListener('pageshow', (event) => {
        if (!event.persisted && !this.submitting) return;
        this.abortCheckout();
        this.setSubmitting(false);
      });

      // Abrir el carrito (p. ej. al agregar un producto) prepara la hoja en segundo plano; al
      // cerrarlo o agregar otro producto, la hoja se cierra con él.
      document.addEventListener('theme:cart-drawer:open', () => this.prefetch());
      document.addEventListener('theme:cart-drawer:close', () => this.close({ restoreFocus: false }));
      document.addEventListener('theme:product:added', () => {
        this.invalidatePrewarm();
        this.close({ restoreFocus: false });
      });
      document.addEventListener('theme:cart:change', () => {
        this.invalidatePrewarm();
        if (!this.isOpen) return;
        this.cartSummary = this.readDrawerSummary() || this.cartSummary;
        if (this.mode === 'delivery' && this.methods.length) this.renderMethods(this.selectedMethodCode());
        if (this.mode === 'pickup') this.renderPickupSummary();
      });
    }

    // Precarga: al abrir el carrito se arma la hoja (ubigeo o tienda guardados, tarifa y
    // documento), así al pulsar «Continuar» ya está lista.
    prefetch() {
      if (this.initialized) return;
      // Sin productos no hay a dónde ir a pagar: no se consulta nada.
      const summary = this.readDrawerSummary();
      if (!summary || !summary.count) return;
      this.cartSummary = summary;
      this.initialized = true;
      this.init();
    }

    get isOpen() {
      return this.sheet.classList.contains('is-open');
    }

    // Capa de GPU para el panel solo mientras sube/baja (se crea antes del primer frame
    // y se libera al terminar, para no retener memoria).
    markSliding() {
      clearTimeout(this.slideTimer);
      this.sheet.classList.add('is-sliding');
      this.slideTimer = setTimeout(() => this.sheet.classList.remove('is-sliding'), 500);
    }

    // Anima la altura del panel cuando cambia su contenido (paso 2, tarifa, tiendas…).
    // ResizeObserver corre tras el layout y antes del pintado: se fija la altura anterior,
    // y en el siguiente frame la nueva, así el salto nunca llega a verse.
    watchHeight() {
      const panel = this.sheet.querySelector('.enova-minicart-sheet__panel');
      const head = this.sheet.querySelector('.enova-minicart-sheet__head');
      const inner = this.sheet.querySelector('[data-enova-checkout-inner]');
      const actions = this.sheet.querySelector('[data-enova-checkout-actions]');
      if (!panel || !head || !inner || !actions || typeof ResizeObserver === 'undefined') return;

      // Altura natural = encabezado + contenido del cuerpo + acciones (sin depender del
      // alto actual del panel, que es justo lo que se anima).
      const naturalHeight = () => head.offsetHeight + inner.offsetHeight + actions.offsetHeight;

      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
      let lastHeight = 0;
      let frame = 0;
      let restTop = null;
      let glideTimer = 0;

      // El botón nunca salta: si un cambio de contenido o el fin de la animación lo mueve de
      // lugar, el pie (resumen + botón) se desliza desde donde estaba (FLIP con transform, antes
      // del pintado). La referencia es el botón: si el resumen cambia de alto, se absorbe arriba.
      const button = actions.querySelector('[data-enova-checkout-submit]') || actions;
      const buttonTop = () => button.getBoundingClientRect().top;
      const glide = (fromTop) => {
        const layoutTop = buttonTop() - (actions.enovaGlideOffset || 0);
        if (fromTop == null || (reduceMotion && reduceMotion.matches)) return layoutTop;
        const delta = fromTop - layoutTop;
        if (Math.abs(delta) < 1) return layoutTop;
        actions.enovaGlideOffset = delta;
        actions.style.transition = 'none';
        actions.style.transform = `translate3d(0, ${delta}px, 0)`;
        requestAnimationFrame(() => {
          actions.enovaGlideOffset = 0;
          actions.style.transition = 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
          actions.style.transform = '';
        });
        clearTimeout(glideTimer);
        glideTimer = setTimeout(() => (actions.style.transition = ''), 340);
        return layoutTop;
      };

      const finish = (event) => {
        if (event && (event.target !== panel || event.propertyName !== 'height')) return;
        const visibleTop = buttonTop();
        panel.style.height = '';
        panel.classList.remove('is-resizing', 'is-pinned');
        panel.style.removeProperty('will-change');
        restTop = this.isOpen ? glide(visibleTop) : null;
      };
      panel.addEventListener('transitionend', finish);

      const observer = new ResizeObserver(() => {
        const height = naturalHeight();
        const previous = lastHeight;
        lastHeight = height;
        if (!previous || previous === height || !this.isOpen || (reduceMotion && reduceMotion.matches)) {
          restTop = this.isOpen ? buttonTop() : null;
          return;
        }
        // Un bloque que se despliega solo (p. ej. el RUC) ya anima su alto: la hoja lo sigue tal cual.
        if (performance.now() < this.heightFrozenUntil) {
          cancelAnimationFrame(frame);
          finish();
          return;
        }

        cancelAnimationFrame(frame);
        // Si ya había una animación en curso, parte desde la altura visible actual.
        const from = panel.style.height ? panel.getBoundingClientRect().height : previous;
        // Durante la animación el botón se mantiene abajo; al terminar, si su lugar en reposo es
        // otro (contenido más alto que la pantalla), se desliza hasta ahí.
        panel.classList.add('is-resizing', 'is-pinned');
        panel.style.willChange = this.sheet.classList.contains('is-sliding') ? 'transform, height' : 'height';
        panel.style.height = `${from}px`;
        restTop = glide(restTop);
        frame = requestAnimationFrame(() => {
          panel.style.height = `${height}px`;
          // Si la altura queda topada por max-height no hay transitionend: se limpia igual.
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(finish, 450);
        });
      });
      [head, inner, actions].forEach((node) => observer.observe(node));
    }

    // Pausa la animación de alto de la hoja mientras un bloque anima el suyo.
    freezeHeightAnimation(duration) {
      this.heightFrozenUntil = performance.now() + duration;
    }

    onTriggerClick(event) {
      const trigger = event.target.closest('[data-minicart-next]');
      if (!trigger || trigger.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      this.trigger = trigger;
      this.open();
    }

    onSheetClick(event) {
      const save = event.target.closest('[data-enova-save]');
      if (save) {
        event.preventDefault();
        if (save.dataset.enovaSave === 'document') this.saveDocument();
        else this.saveDelivery();
        return;
      }
      const summaryRow = event.target.closest('[data-enova-summary]');
      if (summaryRow) {
        event.preventDefault();
        this.expand(summaryRow.dataset.enovaSummary, { focus: true });
        return;
      }
      if (event.target.closest('[data-enova-checkout-close]')) {
        event.preventDefault();
        this.close();
      }
    }

    // keyup en captura: el cart-drawer del tema se cierra con el keyup de Escape, así que se
    // detiene aquí para cerrar solo la hoja.
    onKeyup(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
      }
    }

    // En móvil se evita el zoom al enfocar selects e inputs (font-size < 16px) mientras la hoja
    // está abierta, sin tocar el tamaño de letra.
    setViewportNoZoom(noZoom) {
      const meta = document.querySelector('meta[name="viewport"]');
      if (!meta) return;
      if (this.viewportOriginal == null) {
        this.viewportOriginal = meta.getAttribute('content') || 'width=device-width, initial-scale=1.0';
      }
      const content =
        noZoom && window.innerWidth <= 768 && this.viewportOriginal.indexOf('maximum-scale') === -1
          ? `${this.viewportOriginal}, maximum-scale=1`
          : this.viewportOriginal;
      meta.setAttribute('content', content);
    }

    open() {
      if (this.isOpen) return;
      this.sheet.removeAttribute('inert');
      this.sheet.setAttribute('aria-hidden', 'false');
      this.root.classList.add('has-checkout-sheet');
      this.markSliding();
      requestAnimationFrame(() => this.sheet.classList.add('is-open'));
      document.addEventListener('keyup', this.onKeyup, true);
      this.setViewportNoZoom(true);

      this.clearError();
      this.setSubmitting(false);
      // Montos del carrito al instante desde el pie del mini-cart (se re-renderiza con cada
      // cambio): así el resumen nace completo y no le aparecen líneas después.
      const drawerSummary = this.readDrawerSummary();
      if (drawerSummary) this.cartSummary = drawerSummary;
      if (!this.initialized) {
        this.initialized = true;
        this.init();
      }
      if (this.mode === 'delivery') {
        // Si la tarifa ya se calculó (o se está calculando) para este distrito, se reutiliza: la
        // hoja abre con el resumen completo, sin skeleton. Solo se repintan los montos, que
        // dependen del carrito (envío gratis desde el monto mínimo).
        const district = this.selectedOption('district');
        const sameDistrict = !!district && district.dataset.code === this.ratesUbigeo;
        if (!sameDistrict || (!this.ratesInFlight && !this.methods.length)) this.onDistrictChange();
        else if (!this.ratesInFlight) this.renderMethods(this.selectedMethodCode());
        if (this.shippingPlaceholder) this.refreshCartSummary();
      }
      if (this.mode === 'pickup') {
        this.renderPickupSummary();
        this.fitStores();
        this.schedulePrewarm();
      }

      setTimeout(() => {
        const target = this.compact.delivery
          ? this.submitBtn
          : this.mode
            ? this.form.querySelector('input:checked, select:not(:disabled)')
            : this.modesEl.querySelector('input');
        if (target) target.focus({ preventScroll: true });
      }, 320);
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.markSliding();
      this.sheet.classList.remove('is-open');
      this.sheet.setAttribute('aria-hidden', 'true');
      this.sheet.setAttribute('inert', '');
      this.root.classList.remove('has-checkout-sheet');
      document.removeEventListener('keyup', this.onKeyup, true);
      this.setViewportNoZoom(false);
      // Cerrar mientras se arma el checkout lo cancela.
      if (this.submitting) {
        this.abortCheckout();
        this.setSubmitting(false);
      }
      if (!restoreFocus) return;
      const trigger =
        this.trigger && document.contains(this.trigger) ? this.trigger : this.root.querySelector('[data-minicart-next]');
      if (trigger) trigger.focus({ preventScroll: true });
    }

    init() {
      this.saved = readPrefs();
      if (this.hasDocument) this.restoreDocument();
      this.applySaved();
      this.loadRemotePrefs();
    }

    savedMode() {
      const modalidad = this.saved.modalidad;
      if (modalidad === 'recojo-tienda') return 'pickup';
      return modalidad === 'delivery' ? 'delivery' : null;
    }

    // Quien ya tiene datos guardados ve un resumen en vez del formulario completo.
    applySaved() {
      const saved = this.saved;
      const savedMode = this.savedMode();
      this.compact.delivery =
        savedMode === 'delivery'
          ? !!(saved.departamento && saved.provincia && saved.distrito)
          : savedMode === 'pickup' && !!saved.last_pickup_store_id;
      this.compact.document = this.hasDocument && this.compact.delivery && !!this.readDocument({ mark: false });
      if (savedMode) this.setMode(savedMode);
      this.applyCompact();
    }

    // Con sesión iniciada, lo último que eligió el cliente vive en sus metafields: se trae una vez
    // por sesión y, si el cliente aún no tocó la hoja, reemplaza lo de este navegador.
    loadRemotePrefs() {
      if (!this.customerId) return;
      let request;
      try {
        const hit = sessionStorage.getItem(REMOTE_PREFS_KEY);
        if (hit) request = Promise.resolve(JSON.parse(hit));
      } catch (error) {
        request = null;
      }
      if (!request) {
        request = getJSON(`${API_BASE}?scope=customer-prefs&customer_id=${encodeURIComponent(this.customerId)}`)
          .then((data) => {
            const prefs = data && typeof data === 'object' && !Array.isArray(data) ? Object.assign({}, data) : {};
            if (prefs.modalidad_despacho && !prefs.modalidad) prefs.modalidad = prefs.modalidad_despacho;
            const hasData = Object.keys(prefs).some(
              (key) => key !== 'customer_id' && key !== 'status' && prefs[key] !== '' && prefs[key] != null
            );
            const result = hasData && prefs.status !== 'error' ? prefs : {};
            try {
              sessionStorage.setItem(REMOTE_PREFS_KEY, JSON.stringify(result));
            } catch (error) {
              /* noop */
            }
            return result;
          })
          .catch(() => ({}));
      }

      request.then((remote) => {
        if (!remote || !Object.keys(remote).length || this.touched || this.submitting) return;
        const previous = this.saved;
        const remoteValues = {};
        METAFIELD_KEYS.forEach((key) => {
          if (remote[key] != null && remote[key] !== '') remoteValues[key] = String(remote[key]);
        });
        this.saved = writePrefs(remoteValues);
        const changed = ['modalidad', 'departamento', 'provincia', 'distrito', 'last_pickup_store_id'].some(
          (key) => (previous[key] || '') !== (this.saved[key] || '')
        );
        if (changed) this.reapplySaved();
      });
    }

    // Vuelve a restaurar la hoja con preferencias que llegaron después (metafields).
    reapplySaved() {
      const savedMode = this.savedMode();
      if (!savedMode) return;
      if (savedMode !== this.mode) {
        // Sin modo previo, setMode no abre el formulario: el resumen describe lo guardado.
        this.mode = null;
        if (savedMode === 'delivery') this.ubigeoLoaded = false;
      } else if (savedMode === 'delivery') {
        this.restoreUbigeo();
      }
      // La tienda guardada manda sobre la que estaba marcada.
      this.storesEl.querySelectorAll('input:checked').forEach((input) => (input.checked = false));
      this.applySaved();
      if (savedMode === 'pickup' && this.stores.length) this.renderStores();
    }

    /* ---------------- Resumen compacto ---------------- */

    // Abre o cierra un plegable. Con la hoja abierta se anima (y la hoja sigue su alto cuadro
    // a cuadro); cerrada o al restaurar, cambia directo.
    setCollapse(el, open, animate) {
      if (!el || el.classList.contains('is-open') === open) return;
      if (!animate) {
        el.classList.add('is-instant');
        requestAnimationFrame(() => requestAnimationFrame(() => el.classList.remove('is-instant')));
      }
      el.classList.toggle('is-open', open);
      el.inert = !open;
      el.setAttribute('aria-hidden', open ? 'false' : 'true');
    }

    canAnimate() {
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      return this.isOpen && !reduceMotion;
    }

    // Filas del resumen ↔ formulario: lo resumido se pliega y su fila se despliega a la vez.
    applyCompact() {
      const { delivery, document } = this.compact;
      const animate = this.canAnimate();
      if (animate) this.freezeHeightAnimation(380);
      const states = {
        review: delivery || document,
        'summary-delivery': delivery,
        'summary-document': document,
        modes: !delivery,
        ubigeo: !delivery,
        stores: !delivery,
        document: !document,
      };
      Object.keys(states).forEach((key) => this.setCollapse(this.collapses[key], states[key], animate));
      this.scheduleFitStores(animate ? 320 : 0);
      if (this.subtitleEl) {
        this.subtitleEl.textContent = delivery ? this.msg.msgSubtitleReview : this.msg.msgSubtitle;
      }
      this.renderSummary();
    }

    // Abre una sección del resumen para editarla (o porque lo guardado ya no sirve).
    expand(section, { focus = false } = {}) {
      if (!this.compact[section]) return;
      this.compact[section] = false;
      this.applyCompact();
      if (!focus) return;
      const target =
        section === 'document'
          ? this.documentFields.number
          : this.modesEl.querySelector('input:checked') || this.modesEl.querySelector('input');
      // Foco al terminar de abrirse. En pantallas táctiles no se fuerza el teclado.
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (!target || (coarse && section === 'document')) return;
      setTimeout(() => target.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    collapse(section) {
      if (this.compact[section]) return;
      this.compact[section] = true;
      this.applyCompact();
    }

    // «Guardar selección» aparece bajo el distrito (o las tiendas) cuando la elección está
    // completa; al tocarlo, esa parte se pliega en la fila «Entrega».
    renderSaveLinks() {
      const district = this.selectedOption('district');
      const animate = this.canAnimate();
      if (animate) this.freezeHeightAnimation(360);
      this.setCollapse(this.collapses['save-delivery'], !!(district && district.dataset.code), animate);
      this.setCollapse(this.collapses['save-pickup'], !!this.selectedStore(), animate);
    }

    // Lo guardado aquí reaparece la próxima vez, ya plegado en el resumen.
    saveDelivery() {
      if (this.mode === 'pickup') {
        const store = this.selectedStore();
        if (!store) return;
        this.saved = writePrefs({
          modalidad: 'recojo-tienda',
          last_pickup_store_id: String(store.id),
          tienda_recojo: store.name || '',
        });
      } else {
        const region = this.selectedOption('region');
        const province = this.selectedOption('province');
        const district = this.selectedOption('district');
        if (!region || !province || !district) return;
        this.saved = writePrefs({
          modalidad: 'delivery',
          departamento: region.value,
          provincia: province.value,
          distrito: district.value,
          ubigeo: district.dataset.code || '',
        });
      }
      this.collapse('delivery');
      // El foco pasa a «Cambiar» de la fila recién desplegada (el enlace quedó plegado).
      const row = this.summaryEl.querySelector('[data-enova-summary="delivery"]');
      setTimeout(() => row.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    // «Guardar» dentro del campo: sin factura, en el del documento cuando está completo; con
    // factura, en el del RUC cuando documento y RUC están completos.
    renderDocumentSave() {
      const doc = this.selectedDocument();
      const number = this.documentFields.number.value.trim();
      const needsInvoice = this.documentFields.invoice.checked;
      const numberOk = isDocumentValid(number, doc);
      const rucOk = isDocumentValid(this.documentFields.company.value.trim(), this.documents.company);
      this.toggleSave('number', numberOk && !needsInvoice);
      this.toggleSave('company', numberOk && needsInvoice && rucOk);
    }

    toggleSave(field, visible) {
      const wrap = this.documentEl.querySelector(`[data-enova-save-wrap="${field}"]`);
      if (!wrap) return;
      const button = wrap.querySelector('[data-enova-save]');
      wrap.classList.toggle('has-save', visible);
      button.tabIndex = visible ? 0 : -1;
      button.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    saveDocument() {
      const documentData = this.readDocument();
      if (!documentData) {
        this.showError(this.documentMessage);
        return;
      }
      this.clearError();
      this.saved = writePrefs(documentData.saved);
      this.collapse('document');
      this.schedulePrewarm();
      const row = this.summaryEl.querySelector('[data-enova-summary="document"]');
      setTimeout(() => row.focus({ preventScroll: true }), this.canAnimate() ? 300 : 0);
    }

    setSummaryRow(section, title, detail) {
      const row = this.summaryEl.querySelector(`[data-enova-summary="${section}"]`);
      if (!row) return;
      row.querySelector('[data-enova-summary-title]').textContent = title;
      row.querySelector('[data-enova-summary-detail]').textContent = detail;
      row.setAttribute('aria-label', `${title}. ${detail}. ${row.querySelector('.enova-minicart-review__edit').textContent}`);
    }

    renderSummary() {
      if (this.compact.delivery && this.mode) {
        const modeTitle = this.modesEl.querySelector(`[data-enova-checkout-mode="${this.mode}"] .enova-minicart-mode__title`);
        let detail;
        if (this.mode === 'pickup') {
          const store = this.selectedStore();
          detail = store ? [store.name, store.address].filter(Boolean).join(' · ') : this.saved.tienda_recojo || '';
        } else {
          const pick = (level, key) => (this.selectedOption(level) || {}).value || this.saved[key] || '';
          detail = [pick('district', 'distrito'), pick('province', 'provincia'), pick('region', 'departamento')]
            .filter(Boolean)
            .join(', ');
        }
        this.setSummaryRow('delivery', modeTitle ? modeTitle.textContent.trim() : '', detail);
      }
      if (this.compact.document) {
        const doc = this.selectedDocument();
        const person = `${doc.label} ${this.documentFields.number.value.trim()}`;
        const invoice = this.documentFields.invoice.checked;
        this.setSummaryRow(
          'document',
          invoice ? this.msg.msgReceiptFactura : this.msg.msgReceiptBoleta,
          invoice ? `${this.documents.company.label} ${this.documentFields.company.value.trim()} · ${person}` : person
        );
      }
    }

    setMode(mode) {
      const previous = this.mode;
      this.mode = mode;
      // El resumen describe el modo guardado: si se elige otro, se muestra el formulario.
      if (!mode || (previous && previous !== mode)) this.expand('delivery');
      this.clearError();
      this.modesEl.querySelectorAll('input[name="enova-minicart-mode"]').forEach((input) => {
        input.checked = input.value === mode;
        input.closest('.enova-minicart-mode').classList.toggle('is-active', input.value === mode);
      });
      // Alto del panel que sale: el que entra lo conserva mientras carga su contenido, así la
      // hoja no baja para luego volver a subir (una sola transición, al alto real).
      const outgoing = previous && previous !== mode ? this.panels[previous] : null;
      const heldHeight = outgoing && !outgoing.hidden && this.isOpen ? outgoing.offsetHeight : 0;
      // El documento y el alto conservado se aplican recién al intercambiar los paneles: así el
      // documento se desvanece con el panel que sale y aparece con el que entra.
      this.switchPanels(mode ? this.panels[mode] : null, () => {
        this.placeDocument(mode);
        this.showSummaryFor(mode);
        if (mode === 'pickup' && this.storesPending && !this.stores.length && heldHeight) {
          this.panels.pickup.style.minHeight = `${heldHeight}px`;
        }
      });
      this.form.classList.toggle('has-mode', !!mode);
      if (mode !== 'pickup') this.invalidatePrewarm();
      if (mode === 'pickup') {
        this.renderPickupSummary();
        this.schedulePrewarm();
      }
      // En envío a domicilio el resumen siempre está: si aún no hay tarifa, en skeleton.
      if (mode === 'delivery' && (this.shippingEl.hidden || this.shippingPlaceholder)) {
        this.markShippingUpdating();
        this.refreshCartSummary();
      }

      if (mode === 'delivery' && !this.ubigeoLoaded) this.restoreUbigeo();
      if (mode === 'pickup' && !this.storesLoaded) {
        this.storesLoaded = true;
        this.loadStores();
      }
    }

    restoreUbigeo() {
      this.ubigeoLoaded = true;
      this.loadOptions('region', `scope=departaments&country=${COUNTRY_PROXY_ID}`, this.saved.departamento).then((restored) => {
        if (restored) this.onRegionChange(this.saved);
        else this.expand('delivery');
      });
    }

    releasePanelHeight(panel) {
      if (panel) panel.style.minHeight = '';
    }

    // Transición «fade through» entre paneles: primero se desvanece el que sale (rápido, sin
    // moverse de su lugar), luego se intercambia el contenido —la altura se ajusta una sola
    // vez— y entra el nuevo con un fundido suave. Nunca se ven los dos superpuestos.
    switchPanels(incoming, swap) {
      clearTimeout(this.panelTimer);
      const leaving = Object.values(this.panels).filter((panel) => panel !== incoming && !panel.hidden);
      const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const finish = () => {
        leaving.forEach((panel) => {
          panel.hidden = true;
          panel.classList.remove('is-leaving');
          this.releasePanelHeight(panel);
        });
        swap();
        if (!incoming) return;
        incoming.classList.remove('is-leaving');
        incoming.hidden = false;
        // Recién aquí el panel entrante tiene layout: lo que dependa de medirlo va después.
        if (incoming === this.panels.pickup) this.fitStores();
      };

      if (!leaving.length || !this.isOpen || reduceMotion) {
        finish();
        return;
      }
      leaving.forEach((panel) => panel.classList.add('is-leaving'));
      this.sheet.querySelectorAll('[data-enova-summary-for]:not([hidden])').forEach((el) => el.classList.add('is-leaving'));
      this.panelTimer = setTimeout(finish, 150);
    }

    // Desktop: el resumen va en el pie, sobre el Total. Móvil: al final del cuerpo, así se desplaza
    // con el contenido y la lista de tiendas gana ese alto; en el pie fijo quedan Total y botón.
    placeSummaries() {
      if (this.mobileQuery.matches) {
        if (this.summariesEl.parentElement !== this.innerEl) this.innerEl.appendChild(this.summariesEl);
      } else if (this.summariesEl.parentElement !== this.actionsEl) {
        this.actionsEl.insertBefore(this.summariesEl, this.actionsEl.firstChild);
      }
    }

    // Resumen de montos del modo activo (productos, descuentos, envío o recojo) y su Total.
    showSummaryFor(mode) {
      this.summariesEl.hidden = !mode;
      this.sheet.querySelectorAll('[data-enova-summary-for]').forEach((el) => {
        const active = el.dataset.enovaSummaryFor === mode;
        const wasHidden = el.hidden;
        el.classList.remove('is-leaving');
        el.hidden = !active;
        if (active && wasHidden) this.flash(el, 'is-entering', 280);
      });
      this.renderModeTotal();
    }

    // Total del modo activo, junto al botón: carrito + envío elegido (o recojo gratis). Mientras se
    // calcula la tarifa queda en skeleton; sin envío disponible para el distrito, un guion.
    renderModeTotal() {
      const row = this.totalRow;
      row.hidden = !this.mode;
      if (!this.mode) return;
      const cartTotal = this.cartSummary ? this.cartSummary.total : 0;
      let cents = null;
      let text = null;
      if (this.mode === 'pickup') {
        cents = cartTotal;
      } else {
        const updating = this.shippingEl.classList.contains('is-updating');
        const method = updating ? null : this.selectedMethod();
        if (method) cents = cartTotal + this.shippingCents(method);
        else if (!updating) text = '—';
      }
      const pending = cents === null && text === null;
      const wasPending = row.classList.contains('is-updating');
      row.classList.toggle('is-updating', pending);
      if (pending) row.setAttribute('aria-busy', 'true');
      else row.removeAttribute('aria-busy');
      this.totalValue.style.width = pending ? '72px' : '';
      if (pending) this.totalValue.innerHTML = '&nbsp;';
      else this.totalValue.textContent = text || formatMoney(cents);
      if (wasPending && !pending) this.flash(row, 'is-refreshed');
    }

    // Animación puntual (valores recién calculados, tiendas que llegan, campo RUC): la clase se
    // pone solo si el elemento está a la vista y se retira al terminar. Si quedara puesta, el
    // navegador la volvería a correr cada vez que el panel reaparece.
    flash(el, className, duration = 320) {
      clearTimeout(el.enovaFlashTimer);
      el.classList.remove(className);
      if (!this.isOpen || el.closest('[hidden]')) return;
      void el.offsetWidth;
      el.classList.add(className);
      el.enovaFlashTimer = setTimeout(() => el.classList.remove(className), duration);
    }

    /* ---------------- Documento y comprobante ---------------- */

    bindDocument() {
      const fields = this.documentFields;
      // Desde que el cliente toca el bloque, lo suyo manda sobre lo guardado.
      this.documentEl.addEventListener('input', () => (this.documentTouched = true), true);
      this.documentEl.addEventListener('change', () => (this.documentTouched = true), true);
      fields.type.addEventListener('change', () => {
        fields.type.classList.remove('has-error');
        fields.number.classList.remove('has-error');
        this.applyDocumentType();
      });
      fields.number.addEventListener('input', () => {
        this.clearError();
        fields.number.classList.remove('has-error');
        this.sanitizeField(fields.number, this.selectedDocument());
        this.renderDocumentSave();
      });
      fields.company.addEventListener('input', () => {
        this.clearError();
        fields.company.classList.remove('has-error');
        this.sanitizeField(fields.company, this.documents.company);
        this.renderDocumentSave();
      });
      fields.invoice.addEventListener('change', () => {
        fields.company.classList.remove('has-error');
        this.toggleInvoice(fields.invoice.checked, { focus: true });
      });
      fields.company.maxLength = this.documents.company.maxLen;
      fields.company.inputMode = this.documents.company.numeric === false ? 'text' : 'numeric';
    }

    // Un solo bloque para ambos modos: se mueve al panel activo. En envío va antes del
    // resumen, para que el total quede junto al botón; en recojo, tras las tiendas.
    placeDocument(mode) {
      if (!this.hasDocument) return;
      const panel = mode && this.panels[mode];
      this.documentEl.hidden = !panel;
      if (!panel) return;
      panel.appendChild(this.documentEl);
    }

    selectedDocument() {
      const value = this.documentFields.type.value;
      return this.documents.person.find((doc) => doc.value === value) || this.documents.person[0];
    }

    // Largo y teclado del número según el tipo. Al cambiar de tipo se conserva lo escrito
    // (solo se recorta lo que el nuevo tipo no admite).
    applyDocumentType() {
      const doc = this.selectedDocument();
      const input = this.documentFields.number;
      input.maxLength = doc.maxLen;
      input.inputMode = doc.numeric === false ? 'text' : 'numeric';
      this.sanitizeField(input, doc);
      this.renderDocumentSave();
    }

    sanitizeField(input, doc) {
      const clean = sanitizeDocument(input.value, doc);
      if (clean !== input.value) input.value = clean;
    }

    toggleInvoice(needsInvoice, { focus = false } = {}) {
      this.documentFields.invoice.checked = needsInvoice;
      // Solo se anima cuando lo hace el cliente con la hoja abierta; al restaurar datos, directo.
      // Mientras se despliega, la hoja sigue al contenido cuadro a cuadro: el resumen no salta.
      const animate = focus && this.canAnimate();
      if (animate) this.freezeHeightAnimation(360);
      this.setCollapse(this.documentFields.companyField, needsInvoice, animate);
      this.scheduleFitStores(animate ? 320 : 0);
      this.renderDocumentSave();
      // Foco en el RUC al terminar de desplegarse. En pantallas táctiles no se fuerza: abrir el
      // teclado a mitad de la animación mueve la vista; el cliente toca el campo cuando quiera.
      clearTimeout(this.invoiceFocusTimer);
      const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      if (needsInvoice && focus && !coarse) {
        this.invoiceFocusTimer = setTimeout(
          () => this.documentFields.company.focus({ preventScroll: true }),
          animate ? 300 : 0
        );
      }
    }

    restoreDocument() {
      const saved = this.saved;
      const fields = this.documentFields;
      if (!this.documentTouched) {
        if (this.documents.person.some((doc) => doc.value === saved.tipo_documento)) fields.type.value = saved.tipo_documento;
        fields.number.value = saved.numero_documento || '';
        fields.company.value = saved.ruc || '';
      }
      this.applyDocumentType();
      this.sanitizeField(fields.company, this.documents.company);
      this.toggleInvoice(saved.comprobante === 'factura');
    }

    documentError(doc, value) {
      if (!value) return String(this.msg.msgDocumentRequired || '').replace('__LABEL__', doc.label);
      const length =
        doc.minLen === doc.maxLen
          ? String(doc.minLen)
          : String(this.msg.msgDocumentRange || '').replace('__MIN__', doc.minLen).replace('__MAX__', doc.maxLen);
      const template = doc.numeric === false ? this.msg.msgDocumentAlphanumeric : this.msg.msgDocumentNumeric;
      return String(template || '').replace('__LABEL__', doc.label).replace('__LENGTH__', length);
    }

    // Valida el documento (largo y caracteres por tipo). Devuelve sus atributos de carrito y lo
    // que se guarda, o null; en error marca el campo y deja el mensaje en `this.documentMessage`.
    // Sin el bloque de documento no hay nada que validar.
    readDocument({ mark = true } = {}) {
      this.documentMessage = '';
      this.invalidDocumentField = null;
      if (!this.hasDocument) return { attributes: {}, saved: {}, company: '' };

      const fields = this.documentFields;
      const doc = this.selectedDocument();
      const number = fields.number.value.trim();
      const needsInvoice = fields.invoice.checked;
      const company = this.documents.company;
      const ruc = fields.company.value.trim();
      if (mark) {
        fields.number.classList.remove('has-error');
        fields.company.classList.remove('has-error');
      }
      const invalid = [];

      if (!isDocumentValid(number, doc)) {
        invalid.push(fields.number);
        this.documentMessage = this.documentError(doc, number);
        this.invalidDocumentField = fields.number;
      }
      if (needsInvoice && !isDocumentValid(ruc, company)) {
        invalid.push(fields.company);
        if (!this.documentMessage) {
          this.documentMessage = this.documentError(company, ruc);
          this.invalidDocumentField = fields.company;
        }
      }
      if (mark) invalid.forEach((input) => input.classList.add('has-error'));
      if (this.documentMessage) return null;

      const attributes = {
        'tipo de documento': doc.value,
        'numero de documento': number,
        'comprobante de pago': needsInvoice ? 'factura' : 'boleta',
      };
      if (needsInvoice) attributes.ruc = ruc;
      return {
        attributes,
        // El campo «Empresa» del checkout lleva el RUC con factura y el documento con boleta.
        company: needsInvoice ? ruc : number,
        saved: {
          tipo_documento: doc.value,
          numero_documento: number,
          comprobante: needsInvoice ? 'factura' : 'boleta',
          ruc,
        },
      };
    }

    /* ---------------- Envío a domicilio ---------------- */

    // Llena un select desde el proxy. Devuelve true si pudo restaurar `savedValue`.
    loadOptions(level, query, savedValue) {
      const select = this.selects[level];
      select.disabled = true;
      select.innerHTML = `<option value="">${escapeHTML(this.msg.msgLoading)}</option>`;

      return getProxyList(query, UBIGEO_CACHE_TTL)
        .then((list) => {
          const items = level === 'region' ? list.filter((item) => !EXCLUDED.includes(normalizeName(item.name))) : list;
          let options = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
          items.forEach((item) => {
            options += `<option value="${escapeHTML(item.name)}" data-id="${escapeHTML(item.id)}" data-code="${escapeHTML(item.code)}">${escapeHTML(item.name)}</option>`;
          });
          select.innerHTML = options;
          select.disabled = false;

          if (savedValue && items.some((item) => item.name === savedValue)) {
            select.value = savedValue;
            return true;
          }
          return false;
        })
        .catch(() => {
          select.innerHTML = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
          select.disabled = false;
          return false;
        });
    }

    resetSelect(level) {
      const select = this.selects[level];
      select.innerHTML = `<option value="">${escapeHTML(this.msg.msgSelect)}</option>`;
      select.disabled = true;
      if (level === 'district') this.renderSaveLinks();
    }

    selectedOption(level) {
      const select = this.selects[level];
      return select.value ? select.options[select.selectedIndex] : null;
    }

    onRegionChange(saved = {}) {
      this.clearError();
      this.resetSelect('province');
      this.resetSelect('district');
      this.resetShipping();
      const region = this.selectedOption('region');
      if (!region) return;

      const restoreProvince = saved.departamento === region.value ? saved.provincia : null;
      this.loadOptions(
        'province',
        `scope=provinces&country=${COUNTRY_PROXY_ID}&departament=${encodeURIComponent(region.dataset.id)}`,
        restoreProvince
      ).then((restored) => {
        if (restored) this.onProvinceChange(saved);
        else if (saved.provincia) this.expand('delivery');
      });
    }

    onProvinceChange(saved = {}) {
      this.clearError();
      this.resetSelect('district');
      this.resetShipping();
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      if (!region || !province) return;

      const restoreDistrict = saved.provincia === province.value ? saved.distrito : null;
      this.loadOptions(
        'district',
        `scope=distrits&country=${COUNTRY_PROXY_ID}&departament=${encodeURIComponent(region.dataset.id)}&province=${encodeURIComponent(province.dataset.id)}`,
        restoreDistrict
      ).then((restored) => {
        if (restored) this.onDistrictChange(saved);
        else if (saved.distrito) this.expand('delivery');
      });
    }

    onDistrictChange(saved = {}) {
      this.clearError();
      this.renderSaveLinks();
      const district = this.selectedOption('district');
      if (!district || !district.dataset.code) {
        this.resetShipping();
        return;
      }
      this.fetchRates(district.dataset.code, saved.tipo_envio || this.selectedMethodCode());
    }

    // ¿Ya hay un resumen con total a la vista? Entonces se conserva al recalcular.
    hasShippingSummary() {
      return !this.shippingEl.hidden && !!this.shippingEl.querySelector('.enova-minicart-sheet__method');
    }

    // Departamento o provincia cambió (o se vació el distrito): el resumen se queda con los
    // valores que van a cambiar en skeleton en vez de desaparecer.
    resetShipping() {
      this.ratesRequest += 1;
      this.ratesInFlight = false;
      this.ratesUbigeo = null;
      this.methods = [];
      this.shippingEl.classList.remove('is-loading');
      this.markShippingUpdating();
    }

    // UI optimista: el resumen de envío a domicilio siempre está a la vista. Mientras no hay
    // tarifa, el envío y el total llevan un skeleton sutil; si todavía no hubo ninguna, se pinta
    // la misma estructura con los productos y descuentos reales del carrito.
    markShippingUpdating() {
      this.shippingEl.hidden = false;
      this.shippingEl.setAttribute('aria-busy', 'true');
      this.shippingEl.classList.remove('is-refreshed');
      this.shippingEl.classList.add('is-updating');
      if (!this.hasShippingSummary() || this.shippingPlaceholder) this.renderShippingPlaceholder();
      this.renderModeTotal();
    }

    // { count, original, total } del pie del mini-cart (en céntimos), o null.
    readDrawerSummary() {
      const el = this.root.querySelector('[data-mc-cart-summary]');
      if (!el) return null;
      const read = (key) => Number(el.dataset[key]) || 0;
      return { count: read('count'), original: read('original'), total: read('total') };
    }

    renderShippingPlaceholder() {
      this.shippingPlaceholder = true;
      if (!this.cartSummary) this.cartSummary = this.readDrawerSummary();
      const bar = (width) => `<span class="enova-minicart-sk" style="width:${width}px"></span>`;
      const products = this.cartSummary
        ? this.renderSummaryLines()
        : `<div class="enova-minicart-sheet__line">${bar(72)}${bar(44)}</div>`;
      this.shippingEl.innerHTML = `
        <span class="enova-minicart-sr">${escapeHTML(this.msg.msgCalculating)}</span>
        ${products}
        <div class="enova-minicart-sheet__method is-single">
          <span class="enova-minicart-sheet__method-text">
            <span class="enova-minicart-sheet__method-name">${escapeHTML(this.msg.msgShipping)}</span>
            <span class="enova-minicart-sheet__method-desc" style="width:112px">&nbsp;</span>
          </span>
          <span class="enova-minicart-sheet__method-price" style="width:56px">&nbsp;</span>
        </div>`;
    }

    // Productos y descuentos del carrito para el resumen, aunque aún no haya distrito.
    refreshCartSummary() {
      return getJSON(`${ROOT}cart.js`)
        .then((cart) => {
          const previous = JSON.stringify(this.cartSummary);
          this.cartSummary = {
            count: Number(cart.item_count) || 0,
            original: Number(cart.original_total_price) || 0,
            total: Number(cart.total_price) || 0,
          };
          // Solo se repinta si el carrito cambió: nada se mueve si los montos son los mismos.
          const changed = previous !== JSON.stringify(this.cartSummary);
          if (changed && this.shippingPlaceholder && this.mode === 'delivery') this.renderShippingPlaceholder();
          if (changed && this.mode === 'pickup') this.renderPickupSummary();
        })
        .catch(() => {});
    }

    // Tarifas del distrito (scope=district-calc-rates). No dependen del carrito: se guardan por
    // ubigeo un rato para no consultar el backend cada vez que se abre la hoja.
    fetchRates(ubigeo, savedMethod) {
      const request = ++this.ratesRequest;
      this.methods = [];
      this.ratesUbigeo = ubigeo;
      this.ratesInFlight = true;
      this.shippingEl.classList.add('is-loading');
      this.markShippingUpdating();

      return getProxyList(`scope=district-calc-rates&ubigeo=${encodeURIComponent(ubigeo)}`, RATES_CACHE_TTL)
        .then((list) => {
          if (request !== this.ratesRequest) return;
          this.ratesInFlight = false;
          this.methods = list
            .filter((method) => method.shipping_method_code)
            .map((method) => ({
              code: String(method.shipping_method_code),
              name: method.shipping_method_name || String(method.shipping_method_code),
              price: Number(method.shipping_price) || 0,
              description: resolveMethodDescription(method.description, ubigeo),
            }));
          this.renderMethods(savedMethod);
        })
        .catch(() => {
          if (request !== this.ratesRequest) return;
          this.ratesInFlight = false;
          this.methods = [];
          this.renderMethods();
        });
    }

    // Envío gratis desde settings.free_shipping_limit (mismo criterio que la barra del carrito).
    isFreeShipping() {
      const total = this.cartSummary ? this.cartSummary.total : 0;
      return this.freeShippingLimit > 0 && total >= Math.round(this.freeShippingLimit * 100);
    }

    shippingCents(method) {
      return this.isFreeShipping() ? 0 : Math.round(method.price * 100);
    }

    renderMethods(savedMethod) {
      const wasUpdating = this.shippingEl.classList.contains('is-updating');
      this.shippingPlaceholder = false;
      this.shippingEl.classList.remove('is-loading', 'is-updating');
      this.shippingEl.removeAttribute('aria-busy');
      // Los valores nuevos entran con un fundido corto, sin mover el resto del resumen.
      if (wasUpdating) this.flash(this.shippingEl, 'is-refreshed');
      if (!this.methods.length) {
        this.shippingEl.innerHTML = `<span class="enova-minicart-sheet__shipping-empty">${escapeHTML(this.msg.msgUnavailable)}</span>`;
        this.renderModeTotal();
        this.expand('delivery');
        return;
      }

      const selected = this.methods.some((method) => method.code === savedMethod) ? savedMethod : this.methods[0].code;
      const single = this.methods.length === 1;
      this.shippingEl.innerHTML = this.renderSummaryLines() + this.methods
        .map((method) => {
          const cents = this.shippingCents(method);
          const price = cents ? formatMoney(cents) : this.msg.msgFree;
          const description = method.description
            ? `<span class="enova-minicart-sheet__method-desc">${escapeHTML(method.description)}</span>`
            : '';
          return `
            <label class="enova-minicart-sheet__method${single ? ' is-single' : ''}">
              <input type="radio" name="enova-minicart-method" value="${escapeHTML(method.code)}"${method.code === selected ? ' checked' : ''}${single ? ' hidden' : ''}>
              <span class="enova-minicart-sheet__method-text">
                <span class="enova-minicart-sheet__method-name">${escapeHTML(method.name)}</span>
                ${description}
              </span>
              <span class="enova-minicart-sheet__method-price${cents ? '' : ' is-free'}">${escapeHTML(price)}</span>
            </label>`;
        })
        .join('');
      this.renderTotal();
    }

    // «Productos (n)» a precio de referencia y, si hay descuentos, su línea: así
    // Productos − Descuentos + Envío = Total (igual que el resumen del pie).
    renderSummaryLines() {
      const summary = this.cartSummary;
      if (!summary) return '';
      // Referencia con precios de comparación: la calcula el Liquid del pie del mini-cart
      // (cart.js no expone compare_at_price). Así la hoja muestra lo mismo que el pie.
      const el = this.root.querySelector('[data-mc-cart-summary]');
      const liquidReference = el ? Number(el.dataset.reference) || 0 : 0;
      const reference = Math.max(liquidReference, summary.original, summary.total);
      const savings = reference - summary.total;
      const label = String(this.msg.msgProducts || '').replace('__COUNT__', summary.count);
      let html = `
        <div class="enova-minicart-sheet__line">
          <span>${escapeHTML(label)}</span>
          <span>${escapeHTML(formatMoney(reference))}</span>
        </div>`;
      if (savings > 0) {
        html += `
          <div class="enova-minicart-sheet__line enova-minicart-sheet__line--discount">
            <span>${escapeHTML(this.msg.msgDiscount)}</span>
            <span>−${escapeHTML(formatMoney(savings))}</span>
          </div>`;
      }
      return html;
    }

    // Total = total del carrito (con descuentos) + envío elegido.
    renderTotal() {
      this.renderModeTotal();
    }

    selectedMethodCode() {
      const input = this.shippingEl.querySelector('input[name="enova-minicart-method"]:checked');
      return input ? input.value : null;
    }

    selectedMethod() {
      const code = this.selectedMethodCode();
      return code ? this.methods.find((method) => method.code === code) || null : null;
    }

    /* ---------------- Recojo en tienda ---------------- */

    loadStores() {
      const request = (this.storesRequest = (this.storesRequest || 0) + 1);
      this.storesPending = true;
      if (!this.stores.length) this.storesEl.innerHTML = this.renderStoresSkeleton();

      return getProxyList('scope=shop-addresses', STORES_CACHE_TTL)
        .then((list) => {
          if (request !== this.storesRequest) return;
          this.storesPending = false;
          this.stores = list;
          this.renderStores();
        })
        .catch(() => {
          if (request !== this.storesRequest) return;
          this.storesPending = false;
          this.storesLoaded = false;
          this.stores = [];
          this.storesEl.innerHTML = `<p class="enova-minicart-stores__status">${escapeHTML(this.msg.msgStoresError)}</p>`;
          this.releasePanelHeight(this.panels.pickup);
          if (this.mode === 'pickup') this.expand('delivery');
        });
    }

    // Resumen de recojo, igual que el de envío: productos, descuentos y recojo (gratis).
    renderPickupSummary() {
      const el = this.pickupSummaryEl;
      if (!el) return;
      if (!this.cartSummary) this.cartSummary = this.readDrawerSummary();
      const store = this.selectedStore();
      // La segunda línea está siempre (sin tienda, una indicación): el resumen no cambia de alto
      // al elegir y la lista de tiendas conserva el suyo.
      const description = `<span class="enova-minicart-sheet__method-desc">${escapeHTML(store ? store.name : this.msg.msgStoreHint)}</span>`;
      el.innerHTML = `
        ${this.renderSummaryLines()}
        <div class="enova-minicart-sheet__method is-single">
          <span class="enova-minicart-sheet__method-text">
            <span class="enova-minicart-sheet__method-name">${escapeHTML(this.msg.msgPickupLabel)}</span>
            ${description}
          </span>
          <span class="enova-minicart-sheet__method-price is-free">${escapeHTML(this.msg.msgFree)}</span>
        </div>`;
      this.renderModeTotal();
    }

    // Tarjetas de tienda en skeleton (misma forma que las reales) mientras llegan las tiendas.
    renderStoresSkeleton() {
      const bar = (width) => `<span class="enova-minicart-sk" style="width:${width}px"></span>`;
      const item = `
        <div class="enova-minicart-store is-skeleton" aria-hidden="true">
          <span class="enova-minicart-store__radio"></span>
          <span class="enova-minicart-store__text">${bar(128)}${bar(184)}</span>
        </div>`;
      return `<span class="enova-minicart-sr">${escapeHTML(this.msg.msgLoading)}</span>${item.repeat(4)}`;
    }

    renderStores() {
      const wasSkeleton = !!this.storesEl.querySelector('.is-skeleton');
      // Con el contenido real ya puesto se suelta el alto conservado: la hoja pasa de una vez
      // al alto de las tiendas.
      this.releasePanelHeight(this.panels.pickup);
      if (wasSkeleton) this.flash(this.storesEl, 'is-revealed');
      if (!this.stores.length) {
        this.storesEl.innerHTML = `<p class="enova-minicart-stores__status">${escapeHTML(this.msg.msgStoresError)}</p>`;
        return;
      }

      const current = this.selectedStore();
      const preferred = current ? String(current.id) : String(this.saved.last_pickup_store_id || '');

      this.storesEl.innerHTML = this.stores
        .map((store, index) => {
          const checked = String(store.id) === preferred ? ' checked' : '';
          const reference = store.reference
            ? `<span class="enova-minicart-store__meta">${escapeHTML(store.reference)}</span>`
            : '';
          return `
            <label class="enova-minicart-store">
              <input type="radio" name="enova-minicart-store" value="${index}"${checked}>
              <span class="enova-minicart-store__radio" aria-hidden="true"></span>
              <span class="enova-minicart-store__text">
                <span class="enova-minicart-store__name">${escapeHTML(store.name)}</span>
                <span class="enova-minicart-store__address">${escapeHTML(store.address)}</span>
                ${reference}
              </span>
            </label>`;
        })
        .join('');
      this.renderPickupSummary();
      this.renderSaveLinks();
      this.storesRevealPending = true;
      this.fitStores();
      this.schedulePrewarm();
      if (this.compact.delivery && this.mode === 'pickup') {
        if (this.selectedStore()) this.renderSummary();
        else this.expand('delivery');
      }
    }

    // En recojo la lista de tiendas tiene su propio scroll: su alto máximo es lo que queda libre en
    // la hoja (al tope de su max-height) después del encabezado, los modos, «Guardar selección»,
    // el documento y el resumen. Así la hoja no se desplaza entera —el resumen y el botón quedan
    // quietos— y «Guardar selección» se ve justo bajo la lista, sin bajar hasta el final.
    fitStores() {
      const list = this.storesEl;
      const stores = this.collapses.stores;
      if (this.mode !== 'pickup' || !stores) return;
      // Con el panel aún oculto (fundido entre paneles) o la lista plegada en el resumen no hay
      // nada que medir: se recalcula cuando el panel se muestra o la lista se despliega.
      if (!stores.classList.contains('is-open') || this.panels.pickup.hidden) return;
      const head = this.sheet.querySelector('.enova-minicart-sheet__head');
      const inner = this.sheet.querySelector('[data-enova-checkout-inner]');
      const actions = this.sheet.querySelector('[data-enova-checkout-actions]');
      // Mismo tope que el CSS del panel: max-height: calc(100% - 48px).
      const panelMax = this.sheet.clientHeight - SHEET_TOP_GAP;
      // «Guardar selección» aparece al elegir: su lugar se reserva desde ya para que la lista no
      // se encoja en ese momento (14px de gap − 6px de margen al abrirse).
      const save = this.collapses['save-pickup'];
      const saveChild = save && save.querySelector('.enova-minicart-save');
      const reserve = save && !save.classList.contains('is-open') && saveChild ? saveChild.offsetHeight + 8 : 0;
      // En móvil el resumen va debajo, en el cuerpo: se alcanza con scroll y no le quita alto a la lista.
      const summariesBelow =
        this.summariesEl.parentElement === inner && !this.summariesEl.hidden ? this.summariesEl.offsetHeight + 14 : 0;
      const others = head.offsetHeight + actions.offsetHeight + inner.offsetHeight - list.offsetHeight - summariesBelow + reserve;
      const available = Math.floor(panelMax - others);
      list.style.maxHeight = `${Math.max(available, STORES_MIN_HEIGHT)}px`;
      // Recién con el alto definitivo se puede centrar la tienda guardada (la lista pudo
      // pintarse plegada en el resumen).
      if (this.storesRevealPending) {
        this.storesRevealPending = false;
        this.revealSelectedStore();
      }
      this.renderStoresOverflow();
    }

    scheduleFitStores(delay = 0) {
      clearTimeout(this.fitStoresTimer);
      cancelAnimationFrame(this.fitStoresFrame);
      const run = () => (this.fitStoresFrame = requestAnimationFrame(() => this.fitStores()));
      if (delay) this.fitStoresTimer = setTimeout(run, delay);
      else run();
    }

    // Degradado al pie de la lista mientras quedan tiendas por ver.
    renderStoresOverflow() {
      const list = this.storesEl;
      const scrollable = list.scrollHeight - list.clientHeight > 1;
      list.classList.toggle('is-scrollable', scrollable);
      list.classList.toggle('is-end', !scrollable || list.scrollTop + list.clientHeight >= list.scrollHeight - 1);
    }

    // La tienda guardada (o elegida) queda centrada dentro de la lista, aunque esté al final.
    revealSelectedStore() {
      const input = this.storesEl.querySelector('input[name="enova-minicart-store"]:checked');
      const label = input && input.closest('.enova-minicart-store');
      if (!label) return;
      const list = this.storesEl;
      list.scrollTop = Math.max(0, label.offsetTop - (list.clientHeight - label.offsetHeight) / 2);
      this.renderStoresOverflow();
    }

    selectedStore() {
      const input = this.storesEl.querySelector('input[name="enova-minicart-store"]:checked');
      return input ? this.stores[Number(input.value)] || null : null;
    }

    /* ---------------- Envío del formulario ---------------- */

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.scheduleFitStores();
    }

    // Tikual: el aviso del documento se pinta junto al campo (no solo al pie de la hoja), el
    // campo se resalta y se lleva a la clienta hasta él. Se borra en cuanto empieza a escribir.
    flagDocumentField(field, message) {
      if (!field) return false;
      const hint = this.sheet.querySelector('[data-enova-document-error]');
      const inline = !!(hint && this.documentFields && field === this.documentFields.number);
      if (inline) {
        hint.textContent = message;
        hint.hidden = false;
      }
      field.setAttribute('aria-invalid', 'true');
      field.classList.remove('is-attention');
      void field.offsetWidth;
      field.classList.add('is-attention');
      field.focus({ preventScroll: true });
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
      field.addEventListener('input', () => {
        if (hint) hint.hidden = true;
        field.removeAttribute('aria-invalid');
        field.classList.remove('is-attention');
      }, { once: true });
      return inline;
    }

    clearError() {
      const hint = this.sheet.querySelector('[data-enova-document-error]');
      if (hint) hint.hidden = true;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      Object.values(this.selects).forEach((select) => select.classList.remove('has-error'));
      this.storesEl.classList.remove('has-error');
      this.modesEl.classList.remove('has-error');
    }

    setSubmitting(submitting) {
      this.submitting = submitting;
      this.submitBtn.disabled = submitting;
      this.submitBtn.classList.toggle('is-loading', submitting);
      this.submitBtn.setAttribute('aria-busy', submitting ? 'true' : 'false');
      if (!this.defaultLabel) this.defaultLabel = this.submitLabel.textContent;
      this.submitLabel.textContent = submitting ? this.msg.msgProcessing : this.defaultLabel;
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitting) return;
      this.clearError();

      if (!this.mode) {
        this.modesEl.classList.add('has-error');
        this.showError(this.msg.msgModeRequired);
        return;
      }

      // Se valida todo de una vez; el mensaje es el del primer campo con error (de arriba abajo).
      const documentData = this.readDocument();
      const order = this.mode === 'pickup' ? this.buildPickupOrder(documentData) : this.buildDeliveryOrder(documentData);
      if (!order || !documentData) {
        // Un error dentro del resumen abre esa sección para que se vea qué corregir.
        if (!order) this.expand('delivery');
        if (!documentData) this.expand('document');
        if (!documentData && this.errorEl.hidden) {
          // Tikual: si el aviso puede ir junto al campo, no se repite al pie de la hoja.
          const inline = order && this.flagDocumentField(this.invalidDocumentField, this.documentMessage);
          if (!inline) this.showError(this.documentMessage);
        }
        return;
      }

      this.setSubmitting(true);
      this.goToCheckout(order, documentData);
    }

    baseAttributes(modalidad) {
      return {
        pais: 'Peru',
        'Modalidad despacho': modalidad,
        email: this.customerEmail || 'guest@checkout.local',
      };
    }

    buildDeliveryOrder(documentData) {
      const region = this.selectedOption('region');
      const province = this.selectedOption('province');
      const district = this.selectedOption('district');
      const missing = [
        ['region', region],
        ['province', province],
        ['district', district],
      ].filter(([, option]) => !option);

      if (missing.length) {
        missing.forEach(([level]) => this.selects[level].classList.add('has-error'));
        this.showError(this.msg.msgRequired);
        return null;
      }
      if (this.ratesInFlight) {
        this.showError(this.msg.msgCalculating);
        return null;
      }

      const method = this.selectedMethod();
      if (!method) {
        this.showError(this.msg.msgUnavailable);
        return null;
      }

      const attributes = Object.assign(this.baseAttributes('delivery'), {
        departamento: region.value,
        provincia: province.value,
        distrito: district.value,
        ubigeo: district.dataset.code,
        'tipo de envio': method.code,
      }, documentData ? documentData.attributes : {});

      return {
        modalidad: 'delivery',
        // Plazo del método para el ubigeo (lo que se ve en la hoja), al checkout por línea.
        description: method.description,
        attributes,
        // La dirección exacta se completa en el checkout; aquí va la zona para la tarifa.
        address: {
          city: province.value,
          province: toProvinceCode(region.value),
          zip: district.dataset.code,
          country: 'Peru',
          company: documentData ? documentData.company : '',
        },
        prefs: {
          modalidad: 'delivery',
          departamento: region.value,
          provincia: province.value,
          distrito: district.value,
          ubigeo: district.dataset.code,
          tipo_envio: method.code,
        },
      };
    }

    buildPickupOrder(documentData) {
      const store = this.selectedStore();
      if (!store) {
        this.storesEl.classList.add('has-error');
        this.showError(this.msg.msgStoreRequired);
        return null;
      }
      return this.pickupOrder(store, documentData);
    }

    // Mismos atributos que el recojo del precheckout anterior: la dirección es la de la tienda.
    pickupOrder(store, documentData) {
      const direccion = (store.name || '') + (store.address ? `: ${store.address}` : '');
      const attributes = Object.assign(this.baseAttributes('recojo-tienda'), {
        departamento: store.department || '',
        provincia: store.province || '',
        distrito: store.distrit_name || '',
        ubigeo: store.ubigeo || '',
        direccion,
        'tienda recojo': store.name || '',
      }, documentData ? documentData.attributes : {});

      return {
        modalidad: 'recojo-tienda',
        attributes,
        address: {
          address1: direccion,
          city: store.province || '',
          province: toProvinceCode(store.department) || 'PE-LIM',
          zip: store.ubigeo || store.distrit_name || '',
          country: 'Peru',
          company: documentData ? documentData.company : '',
        },
        prefs: {
          modalidad: 'recojo-tienda',
          last_pickup_store_id: String(store.id),
          tienda_recojo: store.name || '',
        },
      };
    }

    async goToCheckout(order, documentData) {
      this.saved = writePrefs(Object.assign({}, order.prefs, documentData.saved));
      this.saveMetafields();

      const controller = new AbortController();
      this.checkoutController = controller;
      try {
        let checkout = null;
        // Si el carrito de recojo ya se armó en segundo plano para esta misma tienda (y el
        // carrito no cambió), solo queda redirigir.
        if (order.modalidad === 'recojo-tienda' && this.prewarm && this.prewarm.key === this.prewarmKey(order)) {
          checkout = await Promise.race([this.prewarm.promise.catch(() => null), wait(PREWARM_WAIT)]);
        }
        if (!checkout || !checkout.webUrl) {
          checkout = await this.runCheckoutPipeline(order, controller.signal, PICKUP_PRESELECT_TIMEOUT);
        }
        if (controller.signal.aborted) return;
        this.checkoutController = null;
        window.location.href = appendUTMs(checkout.webUrl);
      } catch (error) {
        if (controller.signal.aborted || (error && error.name === 'AbortError')) return;
        this.checkoutController = null;
        try {
          sessionStorage.setItem(
            'minicart_last_error',
            JSON.stringify({ message: error && error.message, when: new Date().toISOString() }, null, 2)
          );
        } catch (storageError) {
          /* noop */
        }
        console.warn('[sifrah-minicart] precheckout falló, se usa el checkout nativo', error);
        window.location.href = appendUTMs(`${ROOT}checkout`);
      }
    }

    abortCheckout() {
      if (!this.checkoutController) return;
      try {
        this.checkoutController.abort();
      } catch (error) {
        /* noop */
      }
      this.checkoutController = null;
    }

    // Preferencias a los metafields del cliente (sin el documento). keepalive: la página se va
    // al checkout enseguida y la petición debe terminar igual.
    saveMetafields() {
      if (!this.customerId) return;
      const prefs = readPrefs();
      const payload = { customer_id: this.customerId };
      METAFIELD_KEYS.forEach((key) => {
        if (prefs[key] != null) payload[key] = prefs[key];
      });
      if (payload.modalidad) payload.modalidad_despacho = payload.modalidad;
      try {
        sessionStorage.setItem(REMOTE_PREFS_KEY, JSON.stringify(payload));
      } catch (error) {
        /* noop */
      }
      fetch(`${API_BASE}?scope=customer-prefs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }

    // cart/update.js (su respuesta ya trae los ítems) y carrito de Storefront listo para el checkout.
    async runCheckoutPipeline(order, signal, preselectTimeout) {
      const attributes = {};
      MODE_ATTRIBUTES.forEach((key) => (attributes[key] = ''));
      Object.assign(attributes, order.attributes);
      const response = await fetch(`${ROOT}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
        signal,
      });
      if (!response.ok) throw new Error('Error al actualizar el carrito');
      const cart = await response.json();
      return this.createCheckout(cart, order, signal, preselectTimeout);
    }

    // Carrito de la Cart API con buyerIdentity y dirección en el mismo cartCreate.
    async createCheckout(cart, order, signal, preselectTimeout) {
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) throw new Error('Carrito vacío');

      const lines = cart.items.map((item) => ({
        merchandiseId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: item.quantity,
        attributes: lineProperties(item).concat([
          { key: '_tipo de despacho', value: order.modalidad },
          { key: '_dispatch_mode_description', value: String(order.description || '') },
          { key: '_shipping method', value: String(order.attributes['tipo de envio'] || '') },
          { key: '_ubigeo', value: String(order.attributes.ubigeo || '') },
        ]),
      }));

      const buyerIdentity = {
        countryCode: 'PE',
        deliveryAddressPreferences: [{ deliveryAddress: cleanAddress(order.address) }],
      };
      const email = String(order.attributes.email || '').trim();
      if (email && email !== 'guest@checkout.local') buyerIdentity.email = email;

      const input = {
        lines,
        buyerIdentity,
        attributes: Object.keys(order.attributes)
          .filter((key) => String(order.attributes[key] || '').trim() !== '')
          .map((key) => ({ key, value: String(order.attributes[key]) })),
      };
      const discountCode = getAppliedDiscountCode(cart);
      if (discountCode) input.discountCodes = [discountCode];

      const data = await storefrontJSON(CART_CREATE, { input }, signal);
      const payload = data && data.cartCreate;
      if (!payload || !payload.cart || (payload.userErrors && payload.userErrors.length)) {
        const message = payload && payload.userErrors && payload.userErrors.length ? payload.userErrors[0].message : 'cartCreate sin carrito';
        throw new Error(message);
      }

      let checkoutUrl = payload.cart.checkoutUrl;
      if (order.modalidad === 'recojo-tienda') {
        try {
          const preselect = this.preselectPickupOption(payload.cart.id, order.address, signal);
          const preselected = preselectTimeout ? await Promise.race([preselect, wait(preselectTimeout)]) : await preselect;
          if (preselected === null) preselect.catch(() => {});
          if (preselected) checkoutUrl = preselected;
        } catch (error) {
          if (error && error.name === 'AbortError') throw error;
          console.warn('[sifrah-minicart] no se pudo preseleccionar el recojo', error);
        }
      }
      return { id: payload.cart.id, webUrl: checkoutUrl };
    }

    // Recojo en tienda: la tarifa «Recojo en Tienda» la calcula el carrier service solo cuando el
    // carrito tiene una dirección de entrega (cartDeliveryAddressesAdd) y se lee junto con las
    // tarifas del carrier (withCarrierRates, con @defer). Sin esto, el checkout preselecciona la
    // primera tarifa de envío en vez del recojo.
    async preselectPickupOption(cartId, address, signal) {
      const added = await storefrontJSON(
        DELIVERY_ADDRESSES_ADD,
        {
          cartId,
          addresses: [
            {
              selected: true,
              address: {
                deliveryAddress: {
                  address1: address.address1 || '',
                  city: address.city || '',
                  company: address.company || '',
                  countryCode: 'PE',
                  provinceCode: address.province || 'PE-LIM',
                  zip: address.zip || '',
                },
              },
            },
          ],
        },
        signal
      );
      const addPayload = added && added.cartDeliveryAddressesAdd;
      if (addPayload && addPayload.userErrors && addPayload.userErrors.length) {
        console.warn('[sifrah-minicart] cartDeliveryAddressesAdd', addPayload.userErrors);
        return null;
      }

      const data = await storefrontDeferred(DELIVERY_GROUPS, { cartId }, signal);
      const edges = (data && data.cart && data.cart.deliveryGroups && data.cart.deliveryGroups.edges) || [];
      const selections = [];
      edges.forEach((edge) => {
        const group = edge.node;
        const pickup = (group.deliveryOptions || []).find((option) => {
          const type = String(option.deliveryMethodType || '').toUpperCase();
          if (type === 'PICKUP' || type === 'PICKUP_POINT' || type === 'LOCAL') return true;
          const title = String(option.title || '').toLowerCase();
          return title.includes('recojo') || title.includes('pickup');
        });
        if (pickup) selections.push({ deliveryGroupId: group.id, deliveryOptionHandle: pickup.handle });
      });
      if (!selections.length) {
        console.warn('[sifrah-minicart] sin tarifa de recojo para preseleccionar');
        return null;
      }

      const selected = await storefrontJSON(SELECTED_DELIVERY_OPTIONS_UPDATE, { cartId, selectedDeliveryOptions: selections }, signal);
      const selPayload = selected && selected.cartSelectedDeliveryOptionsUpdate;
      if (selPayload && selPayload.userErrors && selPayload.userErrors.length) {
        console.warn('[sifrah-minicart] cartSelectedDeliveryOptionsUpdate', selPayload.userErrors);
      }
      return (selPayload && selPayload.cart && selPayload.cart.checkoutUrl) || null;
    }

    /* ---------------- Carrito de recojo en segundo plano ---------------- */

    prewarmKey(order) {
      return JSON.stringify(order.attributes);
    }

    invalidatePrewarm() {
      clearTimeout(this.prewarmTimer);
      if (!this.prewarm) return;
      try {
        this.prewarm.controller.abort();
      } catch (error) {
        /* noop */
      }
      this.prewarm = null;
    }

    schedulePrewarm() {
      clearTimeout(this.prewarmTimer);
      this.prewarmTimer = setTimeout(() => this.maybePrewarmPickup(), 500);
    }

    // La tarifa de recojo tarda varios segundos en el carrier: con la tienda (y el documento,
    // si se pide) completos, el carrito de Storefront se arma antes de pulsar «Ir a pagar».
    maybePrewarmPickup() {
      if (!this.isOpen || this.mode !== 'pickup' || this.submitting) return;
      const store = this.selectedStore();
      const documentData = this.readDocument({ mark: false });
      if (!store || !documentData) return;

      const order = this.pickupOrder(store, documentData);
      const key = this.prewarmKey(order);
      if (this.prewarm && this.prewarm.key === key) return;
      this.invalidatePrewarm();

      const controller = new AbortController();
      const promise = this.runCheckoutPipeline(order, controller.signal, 0);
      promise.catch(() => {
        if (this.prewarm && this.prewarm.key === key) this.prewarm = null;
      });
      this.prewarm = { key, controller, promise };
    }
  }

  function init() {
    captureUTMs();
    const root = document.querySelector('[data-minicart]');
    if (!root || root.dataset.enovaCheckoutReady === 'true') return;
    if (!root.querySelector('[data-enova-checkout-sheet]')) return;
    root.dataset.enovaCheckoutReady = 'true';
    window.sifrahMinicartCheckout = new SifrahMinicartCheckout(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
